import { Controller } from "@hotwired/stimulus"

// Keeps image-dependent action buttons (remix, print, download) disabled until
// the coloring page image is actually on the page. A fresh generation renders
// the image frame as a pulsing placeholder and streams the <img> in later via
// Turbo; we watch for it and enable the gated buttons once it arrives.
//
// Buttons that don't need the image (save, share) aren't marked
// data-image-gated, so they stay active throughout. The server renders the
// gated buttons disabled up front when there's no image yet, so there's no
// clickable flash before this connects.
export default class extends Controller {
  static values = { frameId: String }

  connect() {
    if (this.#imageReady()) return

    // The image streams in via a Turbo Stream "replace", which swaps the whole
    // <turbo-frame> element — so we watch a stable ancestor (the frame's parent)
    // and re-check, rather than observing the frame node that gets replaced.
    const frame = document.getElementById(this.frameIdValue)
    const container = frame?.parentElement
    if (!container) return

    this.observer = new MutationObserver(() => {
      if (this.#imageReady()) {
        this.#enable()
        this.observer.disconnect()
      }
    })
    this.observer.observe(container, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
  }

  #image() {
    return document.getElementById(this.frameIdValue)?.querySelector("img")
  }

  #imageReady() {
    return !!this.#image()
  }

  #enable() {
    // The sidebar's print/download URL was rendered blank (no image at the
    // time); point it at the image that just streamed in so those actions work.
    const img = this.#image()
    if (img) this.element.setAttribute("data-coloring-page-print-image-url-value", img.src)

    this.element.querySelectorAll("[data-image-gated]").forEach((el) => {
      el.removeAttribute("aria-disabled")
      el.removeAttribute("tabindex")
    })
  }
}
